import React from 'react';
import { lazyGettext } from '@eb/lazy-gettext';

import { ScreenSmall } from '@eb/eds-iconography';
import { Lock } from '@eb/eds-iconography';
import { Megaphone } from '@eb/eds-iconography';
import { Eye } from '@eb/eds-iconography';
import { CalendarRepeat } from '@eb/eds-iconography';
import { Stopwatch } from '@eb/eds-iconography';
import { CreditCardBack } from '@eb/eds-iconography';
import { Ticket } from '@eb/eds-iconography';
import { ScanCheck } from '@eb/eds-iconography';
import { Receipt } from '@eb/eds-iconography';
import { Friends } from '@eb/eds-iconography';
import { Money } from '@eb/eds-iconography';
import { LineChart } from '@eb/eds-iconography';
import { Trophy } from '@eb/eds-iconography';
import { Document } from '@eb/eds-iconography';
import { Globe } from '@eb/eds-iconography';
import { ScreenLarge } from '@eb/eds-iconography';
import { Cart } from '@eb/eds-iconography';
import { Row } from '@eb/eds-iconography';
import { Star } from '@eb/eds-iconography';
import { Wifi } from '@eb/eds-iconography';
import { LifePreserver } from '@eb/eds-iconography';
import { Headphone } from '@eb/eds-iconography';
import { Book } from '@eb/eds-iconography';
import { Briefcase } from '@eb/eds-iconography';
import { Gear } from '@eb/eds-iconography';
import { User } from '@eb/eds-iconography';
import { Photo } from '@eb/eds-iconography';
import { CardReader } from '@eb/eds-iconography';
import { Download } from '@eb/eds-iconography';
import { Chat } from '@eb/eds-iconography';

export const MOBILE_OPTIMIZED = {
    heading: lazyGettext('Mobile-optimized event page'),
    description: lazyGettext(
        'Easily create a professional-looking, 100% mobile-optimize event page that looks great on any device.',
    ),
    iconType: <ScreenSmall />,
};

export const SECURE_PAYMENTS = {
    heading: lazyGettext('Secure payments'),
    description: lazyGettext(
        "Accept credit and debit cards without a merchant account using Eventbrite's PCI-compliant payment processor, and pass your fees to attendees.",
    ),
    iconType: <Lock />,
};

export const PROMOTIONAL_TOOLS = {
    heading: lazyGettext('Free promotional tools'),
    description: lazyGettext(
        'Promote your event with free tools like custom email invitations, integrated Facebook promotion, and built-in social sharing.',
    ),
    iconType: <Megaphone />,
};

export const SALES_TRACKING = {
    heading: lazyGettext('Sales at your fingertips'),
    description: lazyGettext(
        "Track sales, counts, and basic attendee info in real-time from any device, so you always know who's coming.",
    ),
    iconType: <Eye />,
};

export const MULTIPLE_EVENTS = {
    heading: lazyGettext('Many events in one'),
    description: lazyGettext(
        'Sell tickets to a series of recurring events — like classes or workshops — on a single event page, so you can efficiently promote and manage your events.',
    ),
    iconType: <CalendarRepeat />,
};

export const INTEGRATIONS_A = {
    heading: lazyGettext('Time-saving integrations'),
    description: lazyGettext(
        "Save time with our seamless integrations with over 170+ tools you already use, like Facebook, Mailchimp, and Raiser's Edge.",
    ),
    iconType: <Stopwatch />,
};

export const INTEGRATIONS_B = {
    heading: lazyGettext('Time-saving integrations'),
    description: lazyGettext(
        'Eliminate manual tasks by seamlessly integrating Eventbrite with 170+ tools you already use, like Salesforce, Mailchimp, and SurveyMonkey.',
    ),
    iconType: <Stopwatch />,
};

export const INTEGRATIONS_C = {
    heading: lazyGettext('Save time with integrations'),
    description: lazyGettext(
        "Eliminate manual tasks by seamlessly integrating Eventbrite with online and on-site tools like Salesforce, Raiser's Edge, and Marcato — or create your own with our open API.",
    ),
    iconType: <Stopwatch />,
};

export const SIMPLE_CHECKOUT = {
    heading: lazyGettext('Simple, secure checkout'),
    description: lazyGettext(
        'Delight your guests with a simple mobile-friendly purchase flow they already trust — no user account, captcha, or login required.',
    ),
    iconType: <CreditCardBack />,
};

export const DIGITAL_TICKETS = {
    heading: lazyGettext('Digital tickets'),
    description: lazyGettext(
        'Let attendees access their tickets from the Eventbrite app, Passbook, Apple Watch, or their email.',
    ),
    iconType: <Ticket />,
};

export const ONE_TICKET_TYPE = {
    heading: lazyGettext('One ticket type'),
    description: lazyGettext(
        'Sell a single type of ticket (for example, "General Admission") at a single price point, for all your attendees.',
    ),
    iconType: <Ticket />,
};

export const FAST_CHECKIN = {
    heading: lazyGettext('Rapid check-in'),
    description: lazyGettext(
        'Use the Eventbrite Organizer app to impress attendees with rapid check-in and ticket sales at the door.',
    ),
    iconType: <ScanCheck />,
};

export const BASIC_TICKETING = {
    heading: lazyGettext('Basic ticketing and registration'),
    description: lazyGettext(
        'Create a professional-looking, 100% mobile-optimized event page to collect payment and basic attendee info.',
    ),
    iconType: <Receipt />,
};

export const SEAMLESS_CHECKIN = {
    heading: lazyGettext('Seamless check-in'),
    description: lazyGettext(
        'Get your attendees in quickly with seamless check-in or at-the-door sales from any device, using our Organizer app',
    ),
    iconType: <ScanCheck />,
};

export const MARKETPLACE = {
    heading: lazyGettext('Reach new audiences'),
    description: lazyGettext(
        'Reach a wider audience and drive sales through our partner sites like Facebook and Spotify.',
    ),
    iconType: <Friends />,
};

export const HIGH_CONVERSION = {
    heading: lazyGettext('4X the conversion'),
    description: lazyGettext(
        'Allow event-goers to purchase tickets wherever they discover your event — converting interest into ticket sales at 4X the average e-commerce rate.',
    ),
    iconType: <Money />,
};

export const CUSTOM_TICKET_OPTIONS = {
    heading: lazyGettext('Customizable ticket options'),
    description: lazyGettext(
        'Create multiple tickets at different prices to attract a wider variety of attendees',
    ),
    iconType: <Ticket />,
};

export const REPORTING_ESSENTIALS = {
    heading: lazyGettext('Reporting essentials'),
    description: lazyGettext(
        'Easily view total sales and RSVPs from any device, and export your detailed order and attendee data.',
    ),
    iconType: <LineChart />,
};

export const DETAILED_ANALYTICS_A = {
    heading: lazyGettext('Detailed analytics'),
    description: lazyGettext(
        'Optimize your marketing with customizable real-time data views on mobile and desktop.',
    ),
    iconType: <LineChart />,
};

export const DETAILED_ANALYTICS_B = {
    heading: lazyGettext('Hone your marketing'),
    description: lazyGettext(
        'Take control of your ticket sales with 24/7 access to real-time data and custom analytics from any device.',
    ),
    iconType: <Trophy />,
};

export const DETAILED_ANALYTICS_C = {
    heading: lazyGettext('Detailed sales analytics'),
    description: lazyGettext(
        'Easily generate actionable insights by ticket type and across multiple events, so you can optimize your marketing.',
    ),
    iconType: <LineChart />,
};

export const MARKETING_TOOLS = {
    heading: lazyGettext('Powerful marketing tools'),
    description: lazyGettext(
        'Extend your marketing with targeted event promotion tools like email marketing, social promotion, and industry-leading SEO.',
    ),
    iconType: <Megaphone />,
};

export const PAYOUTS_A = {
    heading: lazyGettext('Payouts on your schedule'),
    description: lazyGettext(
        'Get paid fast to keep your business liquid, with scheduled payouts before your event.',
    ),
    iconType: <Money />,
};

export const PAYOUTS_B = {
    heading: lazyGettext('Payouts before your event'),
    description: lazyGettext(
        'Apply to get paid weekly or biweekly so you can pay vendors and keep your business liquid.',
    ),
    iconType: <Money />,
    paypalExcl: true,
};

export const PAYOUTS_C = {
    heading: lazyGettext('Get paid on your schedule'),
    description: lazyGettext(
        'Get paid before your event to improve your cash flows and keep vendors in good standing.',
    ),
    iconType: <Money />,
};

export const INSTALLMENT_PAYMENTS = {
    heading: lazyGettext('Installment payments'),
    description: lazyGettext(
        'Increase sales of high-priced tickets by letting buyers pay in monthly installments, at no risk to you.',
    ),
    iconType: <Money />,
};

export const CUSTOM_REGISTRATION = {
    heading: lazyGettext('Customizable registration forms'),
    description: lazyGettext(
        'Learn what you need to about your attendees when they purchase — and avoid surprises on event day.',
    ),
    iconType: <Document />,
};

export const DISTRIBUTION_A = {
    heading: lazyGettext('Reach new audiences'),
    description: lazyGettext(
        'Reach a wider audience and drive sales through our partner sites like Facebook and Spotify.',
    ),
    iconType: <Globe />,
};

export const DISTRIBUTION_B = {
    heading: lazyGettext('Sell where your fans are'),
    description: lazyGettext(
        'Let buyers purchase tickets wherever they discover your event — from your website to Eventbrite to Facebook.',
    ),
    iconType: <Globe />,
};

export const PARTNER_SITES = {
    heading: lazyGettext('Listing on Eventbrite and partner sites'),
    description: lazyGettext(
        'Reach a wider audience and drive sales through our partner sites like Facebook and Spotify.',
    ),
    iconType: <ScreenLarge />,
};

export const CHECKOUT_WIDGET_A = {
    heading: lazyGettext('Sales on your site'),
    description: lazyGettext(
        'Convert hard-earned traffic into sales with our website integrations that make it easy to sell wherever your fans are.',
    ),
    iconType: <Cart />,
};

export const CHECKOUT_WIDGET_B = {
    heading: lazyGettext('Ticket sales on your own site'),
    description: lazyGettext(
        'Convert more interest into ticket buyers by allowing attendees to purchase from your website.',
    ),
    iconType: <Cart />,
    paypalExcl: true,
};

export const CHECKOUT_WIDGET_C = {
    heading: lazyGettext('Make purchases easy'),
    description: lazyGettext(
        'Avoid purchase frustrations with our streamlined, mobile-friendly flow your attendees already know and trust — no user account, captcha, or login required.',
    ),
    iconType: <CreditCardBack />,
};

export const CHECKOUT_WIDGET_D = {
    heading: lazyGettext(
        'Simple, secure checkout on Eventbrite or your website',
    ),
    description: lazyGettext(
        'Avoid purchase frustrations with our streamlined, mobile-friendly flow your attendees already know and trust — no user account, captcha, or login required.',
    ),
    iconType: <CreditCardBack />,
};

export const RESERVED_SEATING = {
    heading: lazyGettext('Reserved seating'),
    description: lazyGettext(
        'Maximize your revenue by creating a map of your venue and charging higher prices for better seats.',
    ),
    iconType: <Row />,
    paypalExcl: true,
};

export const UNLIMITED_TICKET_TYPES = {
    heading: lazyGettext('Unlimited ticket types'),
    description: lazyGettext(
        'Create multiple tickets at different prices and attract a wider variety of attendees.',
    ),
    iconType: <Ticket />,
};

export const VIP_EXPERIENCES_A = {
    heading: lazyGettext('Memorable experiences'),
    description: lazyGettext(
        'Create VIP experiences for your most valued guests with custom registration forms, diverse ticket options, reserved seating, and mobile guest lists.',
    ),
    iconType: <Star />,
};

export const VIP_EXPERIENCES_B = {
    heading: lazyGettext('Delight attendees onsite'),
    description: lazyGettext(
        'Shorten lines and create VIP experience with Organizer app mobile box office and reserved seating',
    ),
    iconType: <Star />,
};

export const ORGANIZER_MOBILE_APP = {
    heading: lazyGettext('Organizer mobile app'),
    description: lazyGettext(
        'Track ticket sales, scan tickets and check-in guests from any iOS or Android device.',
    ),
    iconType: <ScreenSmall />,
};

export const ONLINE_HELP_CENTER = {
    heading: lazyGettext('Online Help Center'),
    description: lazyGettext(
        'Access product "how to\'s" and answers to frequently asked questions in our Help Center.',
    ),
    iconType: <LifePreserver />,
};

export const TRAINING_AND_SUPPORT_A = {
    heading: lazyGettext('Training and support'),
    description: lazyGettext(
        'Get the training and support you need with customized onboarding, Phone support M-F (business hours).',
    ),
    iconType: <Headphone />,
};

export const TRAINING_AND_SUPPORT_B = {
    heading: lazyGettext('Product training'),
    description: lazyGettext(
        'Save time learning the ins and outs of Eventbrite, and ensure you maximize the impact of the platform on your goals.',
    ),
    iconType: <Book />,
};

export const CUSTOMER_SUCCESS_MANGER_SUPPORT = {
    heading: lazyGettext('Customer Success Manager'),
    description: lazyGettext(
        'Get ongoing, personalized support to help you use Eventbrite in the way that best achieves your goals.',
    ),
    iconType: <Headphone />,
};

export const ONSITE_SUPPORT = {
    heading: lazyGettext('On-site staffing and support'),
    description: lazyGettext(
        'Improve your day-of operations with pre-event consultation, on-site technical support, and even full event staffing.',
    ),
    iconType: <Briefcase />,
};

export const LIVE_CHAT_24_7_SUPPORT = {
    heading: lazyGettext('24/7 chat support'),
    description: lazyGettext('24/7 chat support'),
    iconType: <Headphone />,
};
// Currently removed due to Covid-19
export const GENERAL_SUPPORT = {
    heading: lazyGettext('Chat support for event creators with paid events'),
    description: lazyGettext(
        'Get questions answered quickly by talking to a human during business hours.',
    ),
    iconType: <Headphone />,
};
// Currently removed due to Covid-19
export const GENERAL_SUPPORT_HK_SG_NZ = {
    heading: lazyGettext('Chat support for event creators with paid events'),
    description: lazyGettext(
        'Get questions answered quickly by talking to a human during business hours.',
    ),
    iconType: <Headphone />,
};

export const SERVICES_AND_STAFFING = {
    heading: lazyGettext('Services and staffing'),
    description: lazyGettext(
        'Tap into on-site operations support, box office and gate staffing, and custom design services to knock your event out of the park.',
    ),
    iconType: <Friends />,
};

export const RELIABLE_ONSALES = {
    heading: lazyGettext('Reliable onsales'),
    description: lazyGettext(
        'Rely on our 99.99% site uptime to ensure your fans can get tickets during high-demand onsales.',
    ),
    iconType: <Gear />,
};

export const TEAM_PERMISSIONS_A = {
    heading: lazyGettext('Set team access levels'),
    description: lazyGettext(
        'Set your team up with the right permissions, so everyone is equipped to play their role.',
    ),
    iconType: <User />,
};

export const TEAM_PERMISSIONS_B = {
    heading: lazyGettext('Team access and permissions'),
    description: lazyGettext(
        'Work more efficiently across your team, while keeping the right information in the right hands.',
    ),
    iconType: <Friends />,
};

export const WHITE_LABELING_A = {
    heading: lazyGettext('Put your brand front and center'),
    description: lazyGettext(
        'Sell on your website or on new custom community pages to show attendees a fully branded experience.',
    ),
    iconType: <Photo />,
};

export const WHITE_LABELING_B = {
    heading: lazyGettext('Branded community pages'),
    description: lazyGettext(
        'Put your brand front and center with a community landing page and custom branded event templates.',
    ),
    iconType: <Photo />,
};

export const RENT_EQUIPMENT = {
    heading: lazyGettext('Rental equipment for box office and entry'),
    description: lazyGettext(
        'Shorten lines with battle-tested, enterprise-grade tech at your box office, registration table, and gates.',
    ),
    iconType: <CardReader />,
};

export const INCLUDE_ESSENTIALS = {
    heading: lazyGettext('Everything in Essentials'),
    description: lazyGettext(
        'Nail the basics with a 100% mobile-optimized event page, a mobile app, and distribution on partner sites.',
    ),
    iconType: <Download />,
};

export const INCLUDE_ESSENTIALS_WITH_EPP = {
    ...INCLUDE_ESSENTIALS,
    description: lazyGettext(
        'Nail the basics with a 100% mobile-optimised event page, trusted payment processing, a mobile app, and distribution on partner sites',
    ),
};

export const INCLUDE_PROFESSIONAL = {
    heading: lazyGettext('Everything in Professional'),
    description: lazyGettext(
        'Grow your event with a flexible, mobile-optimized event page, an integration to sell on your website, and detailed analytics.',
    ),
    iconType: <Download />,
};

export const PACKAGE_ONE_FEATURES = [
    lazyGettext('One ticket type (ex. "General Admission")'),
    lazyGettext('Ticketing & registration essentials'),
    lazyGettext('Organizer mobile app'),
    lazyGettext('Listing on Eventbrite and partner sites'),
];

export const PACKAGE_ONE_FEATURES_AU_V2 = [
    lazyGettext('Unlimited ticket types'),
    lazyGettext('Ticketing & registration essentials'),
    lazyGettext('Organiser mobile app'),
    lazyGettext('Listing on Eventbrite and partner sites'),
];

export const PACKAGE_ONE_FEATURES_IE_UK = [
    lazyGettext('Ticket fees include payment processing'),
    lazyGettext('Sell tickets directly through Facebook'),
    lazyGettext('One ticket type (e.g. "General Admission")'),
    lazyGettext('Mobile app for organisers'),
    lazyGettext('Listing on Eventbrite and partner sites'),
    lazyGettext('Access to a range of partner apps (e.g. Mailchimp)'),
];

export const PACKAGE_TWO_FEATURES_IE_UK = [
    lazyGettext('Everything in Essentials'),
    lazyGettext('Embedded checkout on your website'),
    lazyGettext('Unlimited ticket types'),
    lazyGettext('Detailed sales analytics and reporting'),
    lazyGettext('Customisable checkout forms'),
    lazyGettext('Branded community pages'),
    lazyGettext('Create seating maps'),
];

export const PACKAGE_THREE_FEATURES_IE_UK = [
    lazyGettext('Everything in Professional, plus:'),
    lazyGettext('Team access and permissions'),
    lazyGettext(
        'Access to preferred partners of rental equipment for box office and entry',
    ),
    lazyGettext('Branded community pages'),
    lazyGettext('Product training'),
];

export const PACKAGE_TWO_FEATURES = [
    lazyGettext('Everything in Essentials, plus:'),
    lazyGettext('Unlimited ticket types'),
    lazyGettext('Ticket sales on your own site'),
    lazyGettext('Customizable checkout forms'),
    lazyGettext('Detailed sales analytics'),
    lazyGettext('Reserved seating'),
];

export const PACKAGE_TWO_FEATURES_AU = [
    lazyGettext('Everything in Essentials, plus:'),
    lazyGettext('Ticket sales on Facebook'),
    lazyGettext('Ticket sales on your own site'),
    lazyGettext('Customizable checkout forms'),
    lazyGettext('Detailed sales analytics'),
    lazyGettext('Reserved seating'),
];

export const PACKAGE_TWO_FEATURES_FOR_CL_CO_PE = [
    lazyGettext('Everything in Essentials, plus:'),
    lazyGettext('Unlimited ticket types'),
    lazyGettext('Ticket sales on your own site'),
    lazyGettext('Customizable checkout forms'),
    lazyGettext('Detailed sales analytics'),
];

export const PACKAGE_TWO_FEATURES_FOR_DK_NO_SE = [
    lazyGettext('Everything in Essentials, plus:'),
    lazyGettext('Unlimited ticket types'),
    lazyGettext('Customizable checkout forms'),
    lazyGettext('Detailed sales analytics'),
    //  lazyGettext('Payouts before your event'),
];

export const PACKAGE_TWO_FEATURES_ALL = [
    lazyGettext('Everything in Essentials, plus:'),
    lazyGettext('Unlimited ticket types'),
    lazyGettext('Ticket sales on your own site'),
    lazyGettext('Customizable checkout forms'),
    lazyGettext('Detailed sales analytics'),
    //  lazyGettext('Payouts before your event'),
    lazyGettext('Reserved seating'),
];

export const PACKAGE_TWO_FEATURES_CH = [
    lazyGettext('Everything in Essentials, plus:'),
    lazyGettext('Unlimited ticket types'),
    lazyGettext('Customizable checkout forms'),
    lazyGettext('Detailed sales analytics'),
    //  lazyGettext('Payouts before your event'),
];

export const PACKAGE_ONE_FEATURES_MARKETPLACE_B = [
    lazyGettext('One ticket type (ex. "General Admission")'),
    lazyGettext('Ticketing & registration essentials'),
    lazyGettext('Organizer mobile app'),
];

export const PACKAGE_TWO_FEATURES_MARKETPLACE_B = [
    lazyGettext('Everything in Essentials, plus:'),
    lazyGettext('Unlimited ticket types'),
    lazyGettext('Public listing on Eventbrite'),
    lazyGettext('Distribution to partner sites'),
    lazyGettext('Ticket sales on your own site'),
    lazyGettext('Customizable checkout forms'),
    lazyGettext('Detailed sales analytics'),
    lazyGettext('Reserved seating'),
];

export const PACKAGE_ONE_GERMAN_FEATURES = [
    lazyGettext('Includes payment transaction for every payment method'),
    lazyGettext('Sell tickets directly through Facebook'),
    lazyGettext('One ticket type (e.g. “Early bird”)'),
    lazyGettext('Mobile app for organizers'),
    lazyGettext('Listing on Eventbrite and partner sites'),
    lazyGettext('Access to a range of partner apps'),
];

export const PACKAGE_TWO_GERMAN_FEATURES = [
    lazyGettext('Everything in Essentials'),
    lazyGettext('Embedded checkout on your website'),
    lazyGettext('Unlimited ticket types'),
    lazyGettext('Detailed sales analytics'),
    lazyGettext('Customisable checkout forms'),
    //  lazyGettext('Payouts before your event'),
    lazyGettext('Reserved seating'),
];

export const PACKAGE_THREE_GERMAN_FEATURES = [
    lazyGettext('Everything in Professional'),
    lazyGettext('Choose your service level'),
    lazyGettext('Product training'),
    lazyGettext('Rental equipment for box office and entry'),
    lazyGettext('Personal Account Manager'),
    lazyGettext('On-site support'),
    lazyGettext('Customized payouts'),
];

export const EVERYTHING_IN_PROFESSIONAL = lazyGettext(
    'Everything in Professional, plus:',
);
export const TEAM_ACCESS = lazyGettext('Team access and permissions');
export const RENTAL_EQUIPMENT = lazyGettext(
    'Rental equipment for box office and entry',
);
export const ON_SITE_STAFFING = lazyGettext('On-site staffing and support');
export const BRAND_PAGES = lazyGettext('Branded community pages');
export const INSTALLMENT = lazyGettext('Installment payments');
export const PRODUCT_TRAINING = lazyGettext('Product training');
export const CUSTOMER_SUCCESS_MANGER = lazyGettext('Customer Success Manager');

export const PACKAGE_THREE_FEATURES = [
    EVERYTHING_IN_PROFESSIONAL,
    TEAM_ACCESS,
    RENTAL_EQUIPMENT,
    BRAND_PAGES,
    PRODUCT_TRAINING,
];

export const PACKAGE_THREE_FEATURES_CH = [
    EVERYTHING_IN_PROFESSIONAL,
    TEAM_ACCESS,
    BRAND_PAGES,
    PRODUCT_TRAINING,
];

export const CUSTOMER_SUPPORT = {
    featureHeading: lazyGettext(
        'Customer Support phone, chat, and email support for all events',
    ),
    iconType: <Chat />,
};
